import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import dispatchMessage from 'app/main/apps/common/AuraFunctions';
import { createInvoiceAdjustmentNote, getInvoiceDetails } from '../../store/orderSlice';

/**
 * PGSS-5306 — raise a credit or debit note against one invoice.
 */
function RaiseAdjustmentNoteDialog({
  open,
  onClose,
  invoiceId,
  defaultAmount,
  defaultNoteType = 'CREDIT',
  discrepancyDecisionId,
  onCreated,
}) {
  const dispatch = useDispatch();
  const [noteType, setNoteType] = useState(defaultNoteType);
  // The discrepancy panel suggests the invoice-vs-order difference. Raising a note changes neither
  // side, so that difference -- and the suggestion -- never go away, and the same amount carried
  // over into every later raise. The suggestion is now used only while the invoice has no note yet;
  // once one exists the field starts empty and staff enter the amount themselves.
  // The panel sums per-line deltas in floating point (71.19 can arrive as 71.19000000000001),
  // so the suggestion is shown to the cent.
  const suggestion = Math.abs(Number(defaultAmount)) > 0 ? Math.abs(Number(defaultAmount)).toFixed(2) : '';
  const [amount, setAmount] = useState('');
  const [raisedNumbers, setRaisedNumbers] = useState([]);
  const [checking, setChecking] = useState(Boolean(suggestion));
  const amountEdited = useRef(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !suggestion || !invoiceId) {
      setChecking(false);
      return undefined;
    }
    let active = true;
    setChecking(true);
    dispatch(getInvoiceDetails({ id: invoiceId }))
      .unwrap()
      .then((res) => {
        if (!active) return;
        const notes = Array.isArray(res?.adjustment_notes) ? res.adjustment_notes : [];
        setRaisedNumbers(notes.map((n) => n?.note_number).filter(Boolean));
        if (notes.length === 0 && !amountEdited.current) {
          setAmount(suggestion);
        }
      })
      // Could not tell whether a note exists: leave the field empty rather than risk a repeat.
      .catch(() => {})
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [open, suggestion, invoiceId, dispatch]);

  let amountHelper;
  if (checking) {
    amountHelper = 'Checking notes already raised on this invoice…';
  } else if (raisedNumbers.length > 0) {
    amountHelper = `Already raised on this invoice: ${raisedNumbers.join(', ')}. Enter an amount only if another note is needed.`;
  }

  const handleSubmit = () => {
    const parsed = Number(amount);
    if (!parsed || parsed <= 0) {
      dispatchMessage(dispatch, 'Enter an amount greater than zero', 'error');
      return;
    }
    if (!reason.trim()) {
      dispatchMessage(dispatch, 'A reason is required', 'error');
      return;
    }
    setSubmitting(true);
    dispatch(createInvoiceAdjustmentNote({
      id: invoiceId,
      note_type: noteType,
      amount: parsed,
      reason: reason.trim(),
      discrepancy_decision_id: discrepancyDecisionId || undefined,
    }))
      .unwrap()
      .then((payload) => {
        dispatchMessage(dispatch, `${noteType === 'CREDIT' ? 'Credit' : 'Debit'} note raised`, 'success');
        if (onCreated) {
          onCreated(payload);
        }
        onClose();
      })
      .catch((message) => {
        dispatchMessage(dispatch, message || 'Unable to raise adjustment note', 'error');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Raise credit or debit note</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" className="mb-16">
          Creates a new document against this invoice. The issued invoice is not rewritten.
          A credit reduces what is owed; a debit increases it.
        </Typography>
        <FormControl component="fieldset" className="mb-16">
          <RadioGroup
            row
            value={noteType}
            onChange={(e) => setNoteType(e.target.value)}
          >
            <FormControlLabel value="CREDIT" control={<Radio />} label="Credit (customer owes less)" />
            <FormControlLabel value="DEBIT" control={<Radio />} label="Debit (customer owes more)" />
          </RadioGroup>
        </FormControl>
        <TextField
          label="Amount"
          type="number"
          value={amount}
          onChange={(e) => {
            amountEdited.current = true;
            setAmount(e.target.value);
          }}
          helperText={amountHelper}
          fullWidth
          className="mb-16"
          inputProps={{ min: 0, step: '0.01' }}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          inputProps={{ maxLength: 1000 }}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting || checking}>
          Raise note
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RaiseAdjustmentNoteDialog;
